import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { NoState } from '@siza-staff-care/shared/data';
import { BehaviorSubject, Observable, tap } from 'rxjs';

export type RequestResponse = 'req' | 'res';

const RequestPrefix = 'req_';
const ResponsePrefix = 'res_';

const MaxSessionStorageLogAge = 60 * 60 * 1000;

let clear = 1;

@Injectable({
	providedIn: 'root',
})
export class SessionStateCleanupStore extends ComponentStore<NoState> {
	private readonly cleared$ = new BehaviorSubject<number>(0);

	/**
	 * Removes old requests and responses from session storage that are older than a certain age.
	 */
	readonly cleanUpSessionStorage = this.effect((maxAgeMs$: Observable<number>) =>
		maxAgeMs$.pipe(
			tap((maxAgeMs) => {
				const now = new Date().getTime();

				const toRemove: string[] = [];
				for (let i = 0; i < sessionStorage.length; i++) {
					const key = sessionStorage.key(i) ?? '';

					if (key.startsWith(RequestPrefix) || key.startsWith(ResponsePrefix)) {
						const time = Number(key.split('_')[1]);
						if (now - time > maxAgeMs) {
							toRemove.push(key);
						}
					}
				}

				toRemove.forEach((key) => sessionStorage.removeItem(key));
				this.cleared$.next(clear++);
			}),
		),
	);

	writeRequestResponseItemToSessionStore(type: RequestResponse, value: string) {
		type += '_';

		const key = type + new Date().getTime().toString();
		this.writeItemToSessionStorageWithRetry(key, value);
	}

	writeItemToSessionStorageWithRetry(key: string, value: string) {
		try {
			sessionStorage.setItem(key, value);
		} catch (e) {
			this.cleanUpSessionStorage(MaxSessionStorageLogAge);

			let stored = false;
			const keys = this.getSessionStorageKeysSorted();
			// Iterate keys in reverse, removing sessionStorage items until we have enough space to store the new item.
			for (let i = keys.length - 1; i >= 0; i--) {
				const key = keys[i];

				if (key.startsWith(RequestPrefix) || key.startsWith(ResponsePrefix)) {
					sessionStorage.removeItem(key);

					try {
						sessionStorage.setItem(key, value);
						stored = true;
						break;
					} catch (e) {
						// ignore
					}
				}
			}

			if (!stored) {
				console.error('Out of space in session storage', e);
			}
		}

		this.cleanUpSessionStorage(MaxSessionStorageLogAge);
	}

	private getSessionStorageKeysSorted() {
		const keys: string[] = [];
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i) ?? '';
			if (key.startsWith(RequestPrefix) || key.startsWith(ResponsePrefix)) {
				keys.push(key);
			}
		}

		return keys.sort();
	}
}
